import React, { useState, useRef, useEffect } from 'react'
import { getApiUrl, getAuthHeaders } from '../utils/api';

const SAFE_RESPONSE_PREVIEW_LEN = 200;
const POLL_INTERVAL_MS = 3000;

const parseApiResponse = async (response, fallbackErrorLabel = 'Request failed') => {
  const rawBody = await response.text();
  let parsedBody = null;
  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.toLowerCase().includes('application/json');

  // Handle non-2xx responses first so we can surface HTTP status-based errors
  if (!response.ok) {
    if (isJson && rawBody) {
      try {
        parsedBody = JSON.parse(rawBody);
      } catch (_) {
        // fall through to generic handling below
      }
    }

    const apiError = parsedBody?.error || parsedBody?.detail || parsedBody?.message;

    // Special handling for common gateway / timeout errors that often return HTML
    if (!apiError) {
      const status = response.status;
      const statusText = response.statusText || '';
      let friendlyMessage = '';

      if (status === 504) {
        friendlyMessage = 'The server took too long to respond (gateway timeout). Please try again in a moment.';
      } else if (status === 502) {
        friendlyMessage = 'Bad gateway from upstream service. Please try again shortly.';
      } else if (status === 503) {
        friendlyMessage = 'Service is temporarily unavailable. Please try again shortly.';
      }

      const snippet = rawBody.slice(0, SAFE_RESPONSE_PREVIEW_LEN).trim();
      const baseStatusMessage = status
        ? `HTTP ${status}${statusText ? `: ${statusText}` : ''}`
        : 'Request failed';

      throw new Error(
        `${fallbackErrorLabel}: ${friendlyMessage || baseStatusMessage}${
          snippet && !snippet.toLowerCase().startsWith('<html')
            ? ` - ${snippet}`
            : ''
        }`
      );
    }

    throw new Error(`${fallbackErrorLabel}: ${apiError}`);
  }

  // 2xx responses – we expect JSON
  if (!rawBody) {
    throw new Error(`${fallbackErrorLabel}: Empty response from server`);
  }

  if (!isJson) {
    const snippet = rawBody.slice(0, SAFE_RESPONSE_PREVIEW_LEN).trim();
    throw new Error(
      `${fallbackErrorLabel}: Unexpected non-JSON response from server.${
        snippet ? ` Body: ${snippet}` : ''
      }`
    );
  }

  try {
    parsedBody = JSON.parse(rawBody);
  } catch (err) {
    const snippet = rawBody.slice(0, SAFE_RESPONSE_PREVIEW_LEN).trim();
    throw new Error(
      `${fallbackErrorLabel}: Failed to parse JSON response.${
        snippet ? ` Body: ${snippet}` : ''
      }`
    );
  }

  return parsedBody;
};

const ApifyExtraction = ({ onExtract, isLoading }) => {
  const [formData, setFormData] = useState({
    reel_url: '',
    platform: 'instagram'
  });
  const [isExtracting, setIsExtracting] = useState(false);
  const [multipleUrls, setMultipleUrls] = useState('');
  const [isExtractingMultiple, setIsExtractingMultiple] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState(null);
  const [extractedCampaigns, setExtractedCampaigns] = useState([]);
  const [singleExtractionStatus, setSingleExtractionStatus] = useState(null);
  const pollingTimers = useRef({});

  useEffect(() => {
    return () => {
      Object.values(pollingTimers.current).forEach(timerId => clearTimeout(timerId));
      pollingTimers.current = {};
    };
  }, []);

  const stopPollingJob = (batchId) => {
    if (!batchId) return;
    const timerId = pollingTimers.current[batchId];
    if (timerId) {
      clearTimeout(timerId);
      delete pollingTimers.current[batchId];
    }
  };

  const fetchJobStatus = async (batchId) => {
    const response = await fetch(`${getApiUrl()}/extract-status/${batchId}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    return parseApiResponse(response, 'Fetch extraction status');
  };

  const startPollingJob = (batchId, { onSuccess, onError, onProgress }) => {
    const poll = async () => {
      try {
        const statusData = await fetchJobStatus(batchId);
        onProgress?.(statusData);

        if (statusData.status === 'succeeded') {
          stopPollingJob(batchId);
          if (onSuccess) {
            await onSuccess(statusData.result);
          }
          return;
        }

        if (statusData.status === 'failed') {
          stopPollingJob(batchId);
          if (onError) {
            await onError(statusData.error || 'Extraction job failed. Please try again.');
          }
          return;
        }
      } catch (error) {
        console.error('Status polling error:', error);
        // If the backend reports that the job doesn't exist, stop polling and surface the error once
        if (error?.message && error.message.toLowerCase().includes('job not found')) {
          stopPollingJob(batchId);
          if (onError) {
            await onError(error.message);
          } else {
            onProgress?.({ status: 'error', error: error.message });
          }
          return;
        }

        // Generic transient error – report but keep polling
        onProgress?.({ status: 'error', error: error.message });
      }

      pollingTimers.current[batchId] = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
  };

  const handleSingleExtractionResult = async (resultPayload) => {
    if (!resultPayload || !resultPayload.success) {
      throw new Error(resultPayload?.error || 'Failed to extract reel data');
    }

    const data = resultPayload;

    if (data.duplicate_found) {
      alert(`✅ Campaign Updated Successfully!\n\n${data.message}\n\nCampaign ID: ${data.campaign_id}\n\nEngagement counts have been automatically updated.`);
      onExtract(data);
      return;
    }

    const extracted = data.extracted_data || {};
    const username =
      (data.campaign_data && data.campaign_data.influencer_username) ||
      extracted.ownerUsername ||
      (extracted.owner && extracted.owner.username) ||
      extracted.username ||
      extracted.authorUsername ||
      (extracted.author && extracted.author.username) ||
      extracted.account_holder_name;

    if (username) {
      try {
        const profResp = await fetch(`${getApiUrl()}/profile-info`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username })
        });
        const profData = await profResp.json();
        if (profData.success && profData.data) {
          data.campaign_data = { ...(data.campaign_data || {}), influencer_info: profData.data };
        }
      } catch (_) {}
    }

    alert(`✅ Data Extracted Successfully!\n\n${data.message}\n\nYou can now complete the campaign details.`);
    onExtract(data);
  };

  const handleBatchExtractionResult = (resultPayload) => {
    if (!resultPayload || !resultPayload.success) {
      throw new Error(resultPayload?.error || 'Batch extraction failed');
    }

    const aggregatedResults = Array.isArray(resultPayload.results) ? resultPayload.results : [];
    const totalSuccessful = resultPayload.successful || 0;
    const totalFailed = resultPayload.failed || 0;
    const totalUrls = resultPayload.total_urls || aggregatedResults.length;

    const successfulCampaigns = aggregatedResults.filter(r => r.success);
    setExtractedCampaigns(successfulCampaigns.map(r => r.campaign_data));
    setExtractionProgress({
      current: totalUrls,
      total: totalUrls,
      message: `✅ Extraction Complete! ${totalSuccessful} successful, ${totalFailed} failed`
    });

    if (successfulCampaigns.length > 0) {
      alert(`✅ Extracted ${totalSuccessful} out of ${totalUrls} URLs successfully!\n\nYou can now complete all campaigns in the form below.`);
    } else {
      alert(`❌ No URLs were extracted successfully. Please check your URLs and try again.`);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleExtractOnly = async (e) => {
    e.preventDefault();
    if (!formData.reel_url) {
      alert('Please enter a reel URL');
      return;
    }

    setIsExtracting(true);
    setSingleExtractionStatus('Queuing extraction job...');
    try {
      const response = await fetch(`${getApiUrl()}/extract-reel-data`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          reel_url: formData.reel_url,
          platform: formData.platform
        }),
      });

      const data = await parseApiResponse(response, 'Extract reel data');

      if (data.batch_id && data.status === 'processing') {
        setSingleExtractionStatus('Processing extraction... this may take ~20s.');
        startPollingJob(data.batch_id, {
          onProgress: (status) => {
            if (status?.status === 'error') {
              setSingleExtractionStatus(`Status check failed: ${status.error}`);
            } else if (status?.status) {
              const label = status.status === 'processing' ? 'Processing' : status.status === 'queued' ? 'Queued' : status.status;
              setSingleExtractionStatus(`Job status: ${label}`);
            }
          },
          onSuccess: async (resultPayload) => {
            await handleSingleExtractionResult(resultPayload);
            setSingleExtractionStatus(null);
            setIsExtracting(false);
          },
          onError: async (errorMessage) => {
            alert(errorMessage);
            setSingleExtractionStatus(errorMessage);
            setIsExtracting(false);
          }
        });
      } else {
        await handleSingleExtractionResult(data);
        setSingleExtractionStatus(null);
        setIsExtracting(false);
      }
    } catch (error) {
      console.error('Error:', error);
      alert(error.message || 'Network error occurred');
      setSingleExtractionStatus(error.message || 'Extraction failed');
      setIsExtracting(false);
    }
  };

  const handleExtractMultiple = async (e) => {
    e.preventDefault();
    if (!multipleUrls.trim()) {
      alert('Please enter at least one URL');
      return;
    }

    // Parse URLs (support both newline and comma separated)
    const urlLines = multipleUrls.split('\n').map(line => line.trim()).filter(line => line);
    const allUrls = [];
    urlLines.forEach(line => {
      // Check if line contains comma-separated URLs
      if (line.includes(',')) {
        line.split(',').forEach(url => {
          const trimmed = url.trim();
          if (trimmed) allUrls.push(trimmed);
        });
      } else {
        allUrls.push(line);
      }
    });

    if (allUrls.length === 0) {
      alert('Please enter at least one valid URL');
      return;
    }

    setIsExtractingMultiple(true);
    setExtractionProgress({ current: 0, total: allUrls.length, message: 'Queuing batch extraction job...' });
    setExtractedCampaigns([]);

    try {
      const response = await fetch(`${getApiUrl()}/extract-multiple-reels`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          urls: allUrls,
          platform: formData.platform
        }),
      });

      const jobData = await parseApiResponse(response, 'Start batch extraction');

      if (jobData.batch_id && jobData.status === 'processing') {
        setExtractionProgress({
          current: 0,
          total: allUrls.length,
          message: `Batch job queued for ${allUrls.length} URLs`
        });

        startPollingJob(jobData.batch_id, {
          onProgress: (status) => {
            if (status?.status === 'error') {
              setExtractionProgress(prev => ({
                ...(prev || { current: 0, total: allUrls.length }),
                message: `Status check failed: ${status.error}`
              }));
            } else if (status?.status) {
              setExtractionProgress(prev => ({
                ...(prev || { current: 0, total: allUrls.length }),
                message: `Job status: ${status.status}`
              }));
            }
          },
          onSuccess: async (resultPayload) => {
            try {
              handleBatchExtractionResult(resultPayload);
            } catch (err) {
              alert(err.message);
            } finally {
              setIsExtractingMultiple(false);
            }
          },
          onError: async (errorMessage) => {
            alert(errorMessage);
            setExtractionProgress(prev => ({
              ...(prev || { current: 0, total: allUrls.length }),
              message: `Job failed: ${errorMessage}`
            }));
            setIsExtractingMultiple(false);
          }
        });
      } else {
        handleBatchExtractionResult(jobData);
        setIsExtractingMultiple(false);
      }
    } catch (error) {
      console.error('Error:', error);
      alert(error.message || 'Network error occurred');
      setExtractionProgress(prev => ({
        ...(prev || { current: 0, total: allUrls.length }),
        message: error.message || 'Batch extraction failed'
      }));
      setIsExtractingMultiple(false);
    }
  };


  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Url Reel Data Extraction</h2>
      
      {/* Single URL Extraction */}
      <div className="mb-8 pb-8 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Single URL Extraction</h3>
      <form 
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          handleExtractOnly(e);
        }}
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Reel URL *
          </label>
          <input
            type="url"
            name="reel_url"
            value={formData.reel_url}
            onChange={handleChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !isExtracting) {
                e.preventDefault();
                handleExtractOnly(e);
              }
            }}
            placeholder="https://www.instagram.com/p/example123/"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Platform
          </label>
          <select
            name="platform"
            value={formData.platform}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="instagram">Instagram</option>
            <option value="tiktok">TikTok</option>
            <option value="youtube">YouTube</option>
            <option value="twitter">Twitter</option>
            <option value="facebook">Facebook</option>
          </select>
        </div>

        <div className="flex justify-center">
          <button
            type="submit"
            disabled={isExtracting}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-lg font-medium"
          >
            {isExtracting ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Extracting Data...
              </>
            ) : (
              'Extract Reel Data'
            )}
          </button>
        </div>
        {singleExtractionStatus && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 text-sm text-blue-800 rounded-md">
            {singleExtractionStatus}
          </div>
        )}
      </form>
      </div>

      {/* Multiple URLs Extraction */}
      <div>
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Multiple URLs Extraction (Batch Processing)</h3>
        <form className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              URLs (one per line or comma-separated) *
            </label>
            <textarea
              value={multipleUrls}
              onChange={(e) => setMultipleUrls(e.target.value)}
              placeholder="https://www.instagram.com/p/example1/
https://www.instagram.com/p/example2/
https://www.instagram.com/p/example3/"
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              Paste multiple URLs, one per line or separated by commas
            </p>
          </div>

          {extractionProgress && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-800">{extractionProgress.message}</span>
                <span className="text-sm text-blue-600">
                  {extractionProgress.current} / {extractionProgress.total}
                </span>
              </div>
              {extractionProgress.total > 0 && (
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(extractionProgress.current / extractionProgress.total) * 100}%` }}
                  ></div>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-center">
            <button
              type="button"
              onClick={handleExtractMultiple}
              disabled={isExtractingMultiple || !multipleUrls.trim()}
              className="w-full bg-green-600 text-white py-3 px-6 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-lg font-medium"
            >
              {isExtractingMultiple ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing URLs...
                </>
              ) : (
                'Extract Multiple URLs'
              )}
            </button>
          </div>
        </form>

        {extractedCampaigns.length > 0 && (
          <div className="mt-6">
            <div className="p-4 bg-green-50 border border-green-200 rounded-md mb-4">
              <p className="text-sm font-medium text-green-800">
                ✅ Successfully extracted {extractedCampaigns.length} campaign(s). 
                Scroll down to complete all campaigns at once.
              </p>
            </div>
            <button
              onClick={() => onExtract({ multiple: true, campaigns: extractedCampaigns })}
              className="w-full bg-purple-600 text-white py-3 px-6 rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 flex items-center justify-center text-lg font-medium"
            >
              Complete All Campaigns ({extractedCampaigns.length})
            </button>
          </div>
        )}
      </div>

      <div className="mt-6 p-4 bg-gray-50 rounded-md">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Supported URL Formats:</h3>
        <ul className="text-xs text-gray-600 space-y-1">
          <li>• Instagram: https://www.instagram.com/p/ABC123/</li>
          <li>• TikTok: https://www.tiktok.com/@user/video/1234567890</li>
          <li>• YouTube: https://www.youtube.com/watch?v=ABC123</li>
          <li>• Twitter: https://twitter.com/user/status/1234567890</li>
          <li>• Facebook: https://www.facebook.com/user/posts/1234567890</li>
        </ul>
      </div>
    </div>
  );
};

export default ApifyExtraction;
